import { useState, useMemo } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import type { SeedVersion } from '@/types'
import { LongRunningOpDialog, makeDeleteInterpreter } from './LongRunningOpDialog'

/**
 * Operator-driven version prune:
 *   1. Modal opens listing all versions of the seed
 *   2. Pre-selects every version older than the 3 most recent + not protected
 *      (deployed / current / share-pinned). Protected versions are disabled
 *      but visible so the operator sees why they can't be removed
 *   3. Operator can override checkboxes
 *   4. Confirm fires the streaming bulk-prune endpoint; the dialog re-mounts
 *      LongRunningOpDialog for live progress
 */

const KEEP_RECENT = 3

export interface PruneVersionsDialogProps {
  open: boolean
  onClose: () => void
  onComplete?: () => void
  seedName: string
  /** All versions of the seed, sorted DESC. */
  versions: SeedVersion[]
  /** Map from version → pinned share count (computed in parent). */
  pinnedShareCount?: Record<string, number>
}

export function PruneVersionsDialog({
  open,
  onClose,
  onComplete,
  seedName,
  versions,
  pinnedShareCount,
}: PruneVersionsDialogProps) {
  // Compute protection state per version. Only `current` and `share-pinned`
  // versions are hard-blocked from prune — `deployed` is just status info and
  // the prune stream auto-decommissions deployed-but-not-current versions
  // before purging them.
  const protection = useMemo(() => {
    const out = new Map<string, { protected: boolean; reason?: string }>()
    versions.forEach((v) => {
      const pinned = pinnedShareCount?.[v.version] ?? 0
      if (v.isCurrent) out.set(v.version, { protected: true, reason: 'current' })
      else if (pinned > 0) out.set(v.version, { protected: true, reason: `pinned by ${pinned} share(s)` })
      else out.set(v.version, { protected: false })
    })
    return out
  }, [versions, pinnedShareCount])

  // Pre-selection: everything not in the most-recent-N window AND not protected.
  // Versions are sorted DESC by the parent; older = later in the array.
  const defaultSelected = useMemo(() => {
    const set = new Set<string>()
    versions.forEach((v, idx) => {
      const p = protection.get(v.version)
      if (idx >= KEEP_RECENT && p && !p.protected) set.add(v.version)
    })
    return set
  }, [versions, protection])

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [running, setRunning] = useState(false)

  // Reset selection whenever the dialog opens.
  useMemo(() => {
    if (open) setSelected(new Set(defaultSelected))
  }, [open, defaultSelected])

  const interpreter = useMemo(() => makeDeleteInterpreter(), [running])

  function toggle(version: string) {
    setSelected(s => {
      const next = new Set(s)
      if (next.has(version)) next.delete(version)
      else next.add(version)
      return next
    })
  }

  const selectedCount = selected.size
  const selectedList = [...selected]

  if (running) {
    return (
      <LongRunningOpDialog
        open={open}
        onClose={() => { setRunning(false); onClose() }}
        onComplete={onComplete}
        title={`Pruning ${selectedCount} version(s) of ${seedName}`}
        description="Per-version: worker delete (cascades DOs), R2 prefixes, guide media, guide approval history and D1 rows."
        confirmLabel="Confirm"
        warning="This cannot be undone."
        request={{
          method: 'POST',
          url: `/api/seeds/${seedName}/versions/prune/stream`,
          body: JSON.stringify({ versions: selectedList }),
        }}
        interpretEvent={interpreter}
      />
    )
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Prune versions of {seedName}</DialogTitle>
          <DialogDescription>
            Pre-selects versions older than the {KEEP_RECENT} most recent. Current
            and share-pinned versions are excluded (can't be pruned). Deployed
            versions are decommissioned automatically by the prune. Override as
            needed, then Confirm to start the streaming prune.
          </DialogDescription>
        </DialogHeader>

        <div className="border rounded max-h-[420px] overflow-y-auto divide-y">
          {versions.map(v => {
            const p = protection.get(v.version)
            const isProtected = p?.protected ?? false
            const isSelected = selected.has(v.version)
            return (
              <label
                key={v.version}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted/30',
                  isProtected && 'opacity-60',
                )}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={isProtected}
                  onChange={() => toggle(v.version)}
                  className="h-4 w-4"
                />
                <span className="font-mono font-medium text-foreground w-16">v{v.version}</span>
                <span className="text-xs text-muted-foreground w-20">
                  {v.deployed ? 'deployed' : 'idle'}
                </span>
                <span className="text-xs text-muted-foreground flex-1 truncate">
                  {v.createdAt ? new Date(v.createdAt).toLocaleDateString() : '—'}
                </span>
                {isProtected && (
                  <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                    {p?.reason}
                  </span>
                )}
              </label>
            )
          })}
        </div>

        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Selected: <strong>{selectedCount}</strong> version(s). Their guide media and guide approval history are also permanently removed. This cannot be undone.
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={() => setRunning(true)}
            disabled={selectedCount === 0}
          >
            Prune {selectedCount} version{selectedCount === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
